// src/pages/admin/AdminAnalytics.jsx
import React, { useMemo } from "react";
import Layout from "../../components/common/Layout";
import { ADMIN_NAV } from "../../utils/navigation";
import {
  useAllUsers,
  useAllLeaveRequests,
  useAllMonthlyAttendance,
  useDepartments,
} from "../../hooks/useFirestore";
import {
  MetricCard,
  DepartmentBreakdownChart,
  ShiftDistributionChart,
  AttendanceTrendChart,
  WorkforceBreakdown,
  LeaveSummaryWidget,
} from "../../components/analytics/AnalyticsWidgets";
import { useShifts, useAllShiftAssignments } from "../../hooks/useFirestoreExtensions";
import { isPresent } from "../../utils/attendanceStatus";

function formatDateKey(date) {
  const yy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

function formatTimeFromMinutes(minutes) {
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const date = new Date();
  date.setHours(hrs, mins, 0, 0);
  return date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function formatWeekLabel(date) {
  return date.toLocaleDateString("en-US", { weekday: "short" }).slice(0, 3);
}

export default function AdminAnalytics() {
  const today = useMemo(() => new Date(), []);
  const year = today.getFullYear();
  const month = today.getMonth() + 1;

  const { data: users, loading: usersLoading } = useAllUsers();
  const { data: leaveRequests, loading: leaveLoading } = useAllLeaveRequests();
  const { data: shifts, loading: shiftsLoading } = useShifts();
  const { data: shiftAssignments, loading: assignmentsLoading } = useAllShiftAssignments();
  const { data: attendance, loading: attendanceLoading } =
    useAllMonthlyAttendance(year, month);
  const { departments } = useDepartments();

  const deptList = useMemo(() => {
    return departments && departments.length > 0
      ? departments.filter((d) => d.active).map((d) => d.name)
      : ["Engineering", "Design", "HR", "QA", "Operations", "Finance"];
  }, [departments]);

  const activeEmployees = useMemo(
    () => users.filter((user) => user.isActive),
    [users],
  );

  const weekDays = useMemo(() => {
    const list = [];
    for (let i = 6; i >= 0; i -= 1) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      list.push({
        iso: formatDateKey(date),
        label: formatWeekLabel(date),
      });
    }
    return list;
  }, [today]);

  const weeklyData = useMemo(() => {
    return weekDays.map((day) => {
      const dailyRecords = attendance.filter((item) => item.date === day.iso);
      const present = dailyRecords.filter(
        (item) => item.mode !== "remote" && isPresent(item),
      ).length;
      const remote = dailyRecords.filter(
        (item) => item.mode === "remote",
      ).length;
      const leave = Math.max(
  0,
  dailyRecords.filter((item) => item.status === "leave").length +
    leaveRequests.filter(
      (request) =>
        request.status === "approved" &&
        request.fromDate <= day.iso &&
        request.toDate >= day.iso,
    ).length,
);

// Count pending or rejected/cancelled leaves for the day
const pendingOrRejected = leaveRequests.filter(
  (request) =>
    (request.status === "pending_lead" ||
      request.status === "pending_admin" ||
      request.status === "rejected" ||
      request.status === "cancelled") &&
    request.fromDate <= day.iso &&
    request.toDate >= day.iso,
).length;

const absent = Math.max(
  0,
  activeEmployees.length - present - remote - leave - pendingOrRejected,
);

      return {
        day: day.label,
        present,
        remote,
        leave,
        absent,
      };
    });
  }, [attendance, leaveRequests, weekDays, activeEmployees.length]);

  const deptData = useMemo(() => {
    return deptList.map((department) => {
      const deptEmployees = activeEmployees.filter(
        (employee) => employee.department === department,
      );
      const total = deptEmployees.length;
      const presentToday = attendance.filter(
        (item) =>
          item.date === formatDateKey(today) &&
          deptEmployees.some((employee) => employee.uid === item.userId) &&
          (isPresent(item) || item.mode === "remote"),
      ).length;
      return {
        department,
        total,
        presentRate: total > 0 ? Math.round((presentToday / total) * 100) : 0,
      };
    });
  }, [activeEmployees, attendance, today, deptList]);

  const recentAttendance = useMemo(
    () =>
      attendance.filter((item) =>
        weekDays.some((day) => day.iso === item.date),
      ),
    [attendance, weekDays],
  );

  const avgCheckInTime = useMemo(() => {
    const validCheckIns = recentAttendance.filter((item) => item.checkIn);
    if (validCheckIns.length === 0) return "—";

    const totalMinutes = validCheckIns.reduce((sum, item) => {
      const time = item.checkIn.toDate();
      return sum + time.getHours() * 60 + time.getMinutes();
    }, 0);

    return formatTimeFromMinutes(
      Math.round(totalMinutes / validCheckIns.length),
    );
  }, [recentAttendance]);

  const avgWorkHours = useMemo(() => {
    const validRecords = recentAttendance.filter(
      (item) => item.checkIn && item.checkOut,
    );
    if (validRecords.length === 0) return "—";

    const totalHours = validRecords.reduce((sum, item) => {
      const hours = item.hoursWorked
        ? item.hoursWorked
        : (item.checkOut.toDate() - item.checkIn.toDate()) / 3_600_000;
      return sum + hours;
    }, 0);

    return `${(totalHours / validRecords.length).toFixed(1)} hrs`;
  }, [recentAttendance]);

  const leaveApprovalRate = useMemo(() => {
    if (leaveRequests.length === 0) return "—";
    const approved = leaveRequests.filter(
      (request) => request.status === "approved",
    ).length;
    return `${Math.round((approved / leaveRequests.length) * 100)}%`;
  }, [leaveRequests]);

  const shiftDistribution = useMemo(() => {
    return shifts.map(s => ({
      name: s.name,
      value: shiftAssignments.filter(a => a.shiftId === s.id).length
    })).filter(s => s.value > 0);
  }, [shifts, shiftAssignments]);

  const workforceStats = useMemo(() => {
    const total = activeEmployees.length;
    const active = attendance.filter(a => a.date === formatDateKey(today) && (isPresent(a) || a.mode === "remote")).length;
    const leave = leaveRequests.filter(r => r.status === "approved" && r.fromDate <= formatDateKey(today) && r.toDate >= formatDateKey(today)).length;
    const remote = attendance.filter(a => a.date === formatDateKey(today) && a.mode === "remote").length;
    return { total, active, leave, remote };
  }, [activeEmployees, attendance, leaveRequests, today]);

  const leaveSummaryData = useMemo(() => {
    const types = ["Sick", "Casual", "Emergency", "Other"];
    const colors = ["#F87171", "#3B82F6", "#FBBF24", "#6B7280"];
    return types.map((type, i) => {
      const count = leaveRequests.filter(r => r.leaveType === type).length;
      const total = leaveRequests.length || 1;
      return {
        type,
        count,
        percentage: (count / total) * 100,
        color: colors[i]
      };
    });
  }, [leaveRequests]);

  const loading = usersLoading || leaveLoading || attendanceLoading || shiftsLoading || assignmentsLoading;

  return (
    <Layout navItems={ADMIN_NAV} panelLabel="Admin Panel">
      <div className="page-header">
        <div>
          <h1 className="page-title">Analytics</h1>
          <p className="page-subtitle">
            Workforce insights and attendance analytics
          </p>
        </div>
      </div>

      <div className="summary-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", marginBottom: "1.25rem" }}>
        <MetricCard
          title="Total Employees"
          value={activeEmployees.length}
          loading={loading}
        />
        <MetricCard
          title="Avg Work Hours"
          value={avgWorkHours}
          loading={loading}
        />
        <MetricCard
          title="Punch-in Avg"
          value={avgCheckInTime}
          loading={loading}
        />
        <MetricCard
          title="Leave Approval"
          value={leaveApprovalRate}
          loading={loading}
        />
      </div>

      <div className="analytics-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: "1.25rem", marginBottom: "1.25rem" }}>
        <AttendanceTrendChart data={weeklyData} loading={loading} />
        <ShiftDistributionChart data={shiftDistribution} loading={loading} />
      </div>

      <div className="analytics-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "1.25rem" }}>
        <WorkforceBreakdown stats={workforceStats} loading={loading} />
        <DepartmentBreakdownChart data={deptData} loading={loading} />
        <LeaveSummaryWidget data={leaveSummaryData} loading={loading} />
      </div>
    </Layout>
  );
}
